import React from 'react';
import { Box, BoxProps } from '@withjoy/joykit';
import { createDisplayName } from '@withjoy/joykit/utils';

interface InternalDividerProps {
  orientation?: 'vertical' | 'horizontal';
}

export interface DividerProps extends Readonly<InternalDividerProps>, BoxProps {}

const _orientationStyles: Record<'horizontal' | 'horizontalWithChildren' | 'vertical' | 'verticalWithChildren', BoxProps> = {
  horizontal: {
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    width: '100%',
  },
  horizontalWithChildren: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: 5,
    _after: {
      content: '""',
      flexGrow: 1,
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderColor: 'mono3',
    },
    _before: {
      content: '""',
      flexGrow: 1,
      borderBottomWidth: '1px',
      borderBottomStyle: 'solid',
      borderColor: 'mono3',
    },
  },
  vertical: {
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
    height: 'auto',
  },
  verticalWithChildren: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    flexDirection: 'column',
    gap: 5,
    _after: {
      content: '""',
      flexGrow: 1,
      borderLeftWidth: '1px',
      borderLeftStyle: 'solid',
      borderColor: 'mono3',
    },
    _before: {
      content: '""',
      flexGrow: 1,
      borderLeftWidth: '1px',
      borderLeftStyle: 'solid',
      borderColor: 'mono3',
    },
  },
};

const Divider: React.FC<DividerProps> = ({ orientation = 'horizontal', children, ...restProps }) => {
  const orientationKey = children ? (`${orientation}WithChildren` as const) : orientation;
  const Element = children ? 'div' : 'hr';
  return (
    <Box
      as={Element}
      role="separator"
      aria-orientation={orientation}
      border={0}
      borderColor={'mono3'}
      {..._orientationStyles[orientationKey]}
      {...restProps}
    >
      {children}
    </Box>
  );
};

Divider.displayName = createDisplayName('Divider');

export { Divider };
